import { formatScore } from './score-format.js';
// "Changes since yesterday" (ENTRAID-347 redesign, implementation plan B2), merged on the client from data that
// exists today:
//   (a) the onGuard score delta between the latest run and the newest run at least 20 hours older;
//   (b) the Microsoft Secure Score delta on the same pair;
//   (c) securityPosture.drift.changes on the latest run (checks that changed status);
//   (d) a failed, partial or incomplete latest collection;
//   (e) Watch events of the last 24 hours, grouped (role assigned, guest invited, risky sign-in, MFA changed, policy
//       changed), only when Watch is on and the viewer is the customer's admin (never an operator account).
// Truthful by construction: no delta without two real values. Rows rank by severity (bad, warn, info, good) and the
// feed returns at most 5 plus the total. Every row has a target the caller turns into a link.

const finite = (n) => typeof n === 'number' && Number.isFinite(n);
const HOUR = 3600000;
export const PAIR_MIN_AGE_MS = 20 * HOUR;
export const WATCH_WINDOW_MS = 24 * HOUR;
export const MAX_ROWS = 5;
const RANK = Object.freeze({ bad: 0, warn: 1, info: 2, good: 3 });
const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const pts = (n) => count(n, 'point', 'points');

const ts = (r) => Date.parse(r?.runTimestamp ?? '');

/** The latest run and the newest run at least 20 hours older than it (runs in any order). */
export function runPair(runs) {
  const sorted = (Array.isArray(runs) ? runs : []).filter((r) => Number.isFinite(ts(r))).sort((a, b) => ts(b) - ts(a));
  const latest = sorted[0] ?? null;
  if (!latest) return { latest: null, previous: null };
  const previous = sorted.find((r) => ts(latest) - ts(r) >= PAIR_MIN_AGE_MS) ?? null;
  return { latest, previous };
}

function scoreRow(latest, previous) {
  const a = previous?.securityPosture?.score;
  const b = latest?.securityPosture?.score;
  if (!finite(a) || !finite(b) || a === b) return null;
  const d = b - a;
  return {
    id: 'score', tone: d > 0 ? 'good' : 'bad', target: { section: 'security' },
    title: `onGuard score ${d > 0 ? 'up' : 'down'} ${pts(Math.abs(d))}`, detail: `${formatScore(a)} to ${formatScore(b)}`,
  };
}

function secureScoreRow(latest, previous) {
  const a = previous?.securityPosture?.msSecureScore;
  const b = latest?.securityPosture?.msSecureScore;
  if (!finite(a?.currentScore) || !finite(b?.currentScore)) return null;
  const d = Math.round((b.currentScore - a.currentScore) * 10) / 10;
  if (d === 0) return null;
  const max = finite(b.maxScore) ? ` of ${b.maxScore}` : '';
  return {
    id: 'secure-score', tone: d > 0 ? 'good' : 'bad', target: { section: 'security' },
    title: `Secure Score ${d > 0 ? 'up' : 'down'} ${pts(Math.abs(d))}`,
    detail: `${a.currentScore.toFixed(1)} to ${b.currentScore.toFixed(1)}${max}`,
  };
}

const titlesLine = (list) => (list.length === 1 ? list[0].title : `${list[0].title} and ${list.length - 1} more`);

function driftRows(latest) {
  const changes = latest?.securityPosture?.drift?.changes;
  if (!Array.isArray(changes)) return [];
  const valid = changes.filter((c) => c && typeof c.title === 'string');
  const groups = [
    { to: 'fail', tone: 'bad', one: 'check now failing', many: 'checks now failing' },
    { to: 'warn', tone: 'warn', one: 'check now warning', many: 'checks now warning' },
    { to: 'pass', tone: 'good', one: 'check now passing', many: 'checks now passing' },
  ];
  return groups.flatMap((g) => {
    const list = valid.filter((c) => c.to === g.to);
    if (list.length === 0) return [];
    return [{
      id: `drift-${g.to}`, tone: g.tone, title: count(list.length, g.one, g.many), detail: titlesLine(list),
      target: { section: 'security', search: { tab: 'findings' } },
    }];
  });
}

function collectionRow(latest) {
  if (!latest) return null;
  const failedNames = (latest.failed ?? []).map((f) => (typeof f === 'string' ? f : f?.name)).filter(Boolean);
  const detail = failedNames.length ? `Not collected: ${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? ` and ${failedNames.length - 3} more` : ''}` : null;
  const target = { section: 'data-collection' };
  if (latest.statusCode === 207) return { id: 'collection', tone: 'warn', title: 'Last collection was partial', detail, target };
  if (latest.statusCode === 206) return { id: 'collection', tone: 'warn', title: 'Last collection timed out', detail, target };
  if (latest.statusCode != null && latest.statusCode !== 200) return { id: 'collection', tone: 'bad', title: 'Last collection failed', detail, target };
  if (latest.truncated === true || latest.incomplete === true) return { id: 'collection', tone: 'warn', title: 'Last collection was incomplete', detail, target };
  return null;
}

/** Watch event groups (catalog ids from the collector's catalog). */
export const WATCH_GROUPS = Object.freeze([
  { id: 'role', ids: ['role.assigned', 'pim.activation'], one: 'new role assignment', many: 'new role assignments' },
  { id: 'guest', ids: ['guest.invited'], one: 'new guest user', many: 'new guest users' },
  { id: 'risky', ids: ['self.risky-sign-in'], one: 'risky sign-in', many: 'risky sign-ins' },
  { id: 'mfa', ids: ['self.per-user-mfa-changed', 'self.security-info-added', 'self.security-info-removed', 'self.security-info-changed'], one: 'MFA change', many: 'MFA changes' },
  { id: 'policy', ids: ['tenant.conditional-access-changed', 'tenant.auth-methods-policy-changed'], one: 'policy change', many: 'policy changes' },
]);

const SEVERITY_TONE = (sevs) => (sevs.includes('critical') ? 'bad' : sevs.includes('high') ? 'warn' : 'info');

function watchRows(events, now) {
  const recent = (Array.isArray(events) ? events : [])
    .filter((e) => e && e.noticeClass !== 'admin-notice')
    .filter((e) => {
      const t = Date.parse(e.activityDateTime ?? '');
      return Number.isFinite(t) && now - t <= WATCH_WINDOW_MS && t <= now + HOUR;
    })
    .sort((a, b) => Date.parse(b.activityDateTime) - Date.parse(a.activityDateTime));
  return WATCH_GROUPS.flatMap((g) => {
    const list = recent.filter((e) => g.ids.includes(e.catalogId));
    if (list.length === 0) return [];
    const [first] = list;
    const unanswered = list.filter((e) => e.answerState === 'unanswered').length;
    const what = typeof first.what === 'string' && first.what ? first.what : null;
    const tail = list.length === 1
      ? (unanswered ? 'Not answered.' : null)
      : (unanswered ? `${unanswered} not answered.` : null);
    const detail = [what ? (list.length > 1 ? `Latest: ${what}` : what) : null, tail].filter(Boolean).join(' ') || null;
    return [{
      id: `watch-${g.id}`, tone: SEVERITY_TONE(list.map((e) => e.severity)), title: count(list.length, g.one, g.many), detail,
      target: list.length === 1 ? { section: 'activity', eventId: first.eventId } : { section: 'activity' },
    }];
  });
}

/**
 * @param {{ runs?: object[], events?: object[]|null, now?: number }} input
 *   `events` is null when Watch content is not read (Watch off, or an operator account).
 * @returns {{ rows: object[], total: number }}
 */
export function changesFeed({ runs = [], events = null, now = Date.now() } = {}) {
  const { latest, previous } = runPair(runs);
  const all = [
    ...(events ? watchRows(events, now) : []),
    ...driftRows(latest),
    collectionRow(latest),
    scoreRow(latest, previous),
    secureScoreRow(latest, previous),
  ].filter(Boolean);
  const ranked = all
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (RANK[a.r.tone] - RANK[b.r.tone]) || (a.i - b.i))
    .map(({ r }) => r);
  return { rows: ranked.slice(0, MAX_ROWS), total: ranked.length };
}
